import type {
  Box,
  Client,
  Highlighter,
  HighlighterNote,
  HighlighterSurveyAdminRead,
  HighlighterTag,
  Survey,
  SurveyInstanceRead,
} from './rtk/rtk.generated';
import { SURVEY_STATUS } from './constants';

export const mockHighlighter = {
  id: '1',
  email: 'john@email.com',
  firstName: 'John',
  lastName: 'Doe',
  status: 'active',
  dateOfBirth: '1980-07-26',
} as const satisfies Highlighter;

export const mockHLTag = {
  id: '123-Tag-ID',
  description: 'Some TagDescription',
  title: 'Some Tag Title',
  createdAt: '2021-07-26T06:59:00.000Z',
} as const satisfies HighlighterTag;

export const mockHLNote = {
  id: '123-Note-ID',
  note: 'Some Note',
  createdAt: '2021-07-26T06:59:00.000Z',
} as const satisfies HighlighterNote;

const mockProject = {
  id: '45484528-ae71-4a51-9822-321888b43a9e',
  name: 'Return of the Obra Dinn - Nautical non-linear puzzle',
  clientPortalId: 1234,
} as const;

export const mockHighlighterBoxes: Box[] = [
  {
    id: 'box-id-1',
    highlighterId: '1',
    kitId: 1,
    project: mockProject,
    deliveryStatus: 'Delivered',
    deliveryCarrier: 'UPS',
    expectedDelivery: new Date('2023-07-26T06:59:00.000Z').toString(),
    trackingNumber: 'tracking1Number',
    createdAt: new Date('2023-08-26T06:59:00.000Z').toString(),
  },
  {
    id: 'box-id-2',
    highlighterId: '2',
    kitId: 1,
    project: mockProject,
    deliveryStatus: 'InTransit',
    deliveryCarrier: 'UPS',
    expectedDelivery: new Date('2023-07-26T06:59:00.000Z').toString(),
    trackingNumber: 'tracking1Number',
    createdAt: new Date('2023-08-26T06:59:00.000Z').toString(),
  },
];

export const mockHlBox = mockHighlighterBoxes[0];

export const mockHLClients = [
  {
    id: '45484528-ae71-4a51-9822-321888b43a9e',
    name: 'Return of the Obra Dinn - Nautical non-linear puzzle',
    clientPortalId: null,
    createdAt: '0001-01-01T00:00:00+00:00',
    updatedAt: '0001-01-01T00:00:00+00:00',
  },
  {
    id: 'b0906656-a3c4-4293-b0f5-5ac82b87ac28',
    name: 'Brotato - indie arena shooter',
    clientPortalId: 123,
    createdAt: '0001-01-01T00:00:00+00:00',
    updatedAt: '0001-01-01T00:00:00+00:00',
  },
] as const satisfies Client[];

export const mockDate = '2023-07-26T06:59:00.000Z';

const mockNewOneOffSurvey = {
  id: '64d4f64a-66f4-4a27-a0b9-e9c93bd42722',
  name: 'test',
  type: 'oneOff',
  deadline: mockDate,
  durationDays: null,
  linkUrl: 'https://test.com/',
} as const satisfies Survey;

const mockNewOptInSurvey = {
  id: '0-opt-in',
  name: 'Opt In Survey',
  type: 'optInSurvey',
  deadline: mockDate,
  durationDays: null,
  linkUrl: 'https://linkUrl.com/',
} as const satisfies Survey;

const mockNewItemSurvey = {
  id: '0-item',
  name: 'Item Survey',
  type: 'itemSurvey',
  deadline: mockDate,
  durationDays: 5,
  linkUrl: 'https://surveyUrl.com/',
  clientId: 'abc',
  client: mockHLClients[1],
} as const satisfies Survey;

export { mockNewOneOffSurvey, mockNewOptInSurvey, mockNewItemSurvey };

export const mockItemSurveys = [
  { ...mockNewItemSurvey, id: '1-item', name: 'Item Survey 1' },
  { ...mockNewItemSurvey, id: '2-item', name: 'Item Survey 2' },
  { ...mockNewItemSurvey, id: '3-item', name: 'Item Survey 3' },
  { ...mockNewItemSurvey, id: '4-item', name: 'Item Survey 4' },
] as const;

export const mockOptInSurveys = [
  { ...mockNewOptInSurvey, id: '1-opt-in', name: 'Opt In Survey 1' },
  { ...mockNewOptInSurvey, id: '2-opt-in', name: 'Opt In Survey 2' },
  { ...mockNewOptInSurvey, id: '3-opt-in', name: 'Opt In Survey 3' },
  { ...mockNewOptInSurvey, id: '4-opt-in', name: 'Opt In Survey 4' },
] as const;

export const mockOneOffSurveys = [
  {
    ...mockNewOneOffSurvey,
    id: '1-one-off',
    name: 'One Off Survey 1',
    internalName: 'InternalName One Off Survey 1',
    linkUrl: 'https://www.google.com',
    imageUrl: 'https://www.google.com',
  },
  {
    ...mockNewOneOffSurvey,
    id: '2-one-off',
    name: 'One Off Survey 2',
    internalName: 'InternalName One Off Survey 2',
    linkUrl: 'https://www.google.com',
    client: mockHLClients[1],
  },
  {
    ...mockNewOneOffSurvey,
    id: '3-one-off',
    name: 'One Off Survey 3 with a super long name maybe just in case we care about it',
    linkUrl: 'https://www.thing.com',
  },
  {
    id: '4-one-off',
    type: 'oneOff',
    name: 'One Off Survey 4',
    linkUrl: 'https://www.google.com',
    deadline: '2023-07-25 21:51:05.966178+00',
  },
] as const;

export const mockSurveys = [...mockItemSurveys, ...mockOneOffSurveys, ...mockOptInSurveys] as const;

export const mockSurveyInstances: SurveyInstanceRead[] = [
  {
    id: '1-survey-instance-id',
    customerId: '1',
    email: 'customer-1@gmail.com',
    surveyId: '1',
    status: SURVEY_STATUS.Sent,
    deadline: '2022-03-31T06:59:00.000Z',
    box: { expectedDelivery: '2023-05-11T03:29:02.000Z' },
    createdAt: '2021-07-26T06:59:00.000Z',
    updatedAt: '2021-07-26T06:59:00.000Z',
    isVisibleToHighlighter: true,
  },
  {
    id: '2-survey-instance-id',
    customerId: '1',
    email: 'customer-1@gmail.com',
    surveyId: '2',
    status: SURVEY_STATUS.Started,
    deadline: '2022-02-21T06:59:00.000Z',
    box: { expectedDelivery: '2022-02-01T03:21:01.000Z' },
    createdAt: '2021-07-26T06:59:00.000Z',
    updatedAt: '2021-07-26T06:59:00.000Z',
    isVisibleToHighlighter: true,
  },
  {
    id: '3-survey-instance-id',
    customerId: '2',
    email: 'customer-2@gmail.com',
    surveyId: '1',
    status: SURVEY_STATUS.Sent,
    createdAt: '2021-07-26T06:59:00.000Z',
    updatedAt: '2021-07-26T06:59:00.000Z',
    isVisibleToHighlighter: false,
  },
  {
    id: '4-survey-instance-id',
    customerId: '3',
    email: 'customer-3@gmail.com',
    surveyId: '3',
    status: SURVEY_STATUS.Started,
    createdAt: '2021-07-26T06:59:00.000Z',
    updatedAt: '2021-07-26T06:59:00.000Z',
    isVisibleToHighlighter: false,
  },
];

export const mockAdminHighlighterSurveys: HighlighterSurveyAdminRead[] = [
  {
    id: '1-one-off',
    name: 'One Off Survey 1',
    type: 'oneOff',
    linkUrl: 'https://www.google.com',
    deadline: '2023-07-26T06:59:00.000Z',
    isOverdue: true,
    status: 'sent',
    project: undefined,
    isVisible: false,
    surveyId: '1-survey-id',
    isLegacy: false,
  },
  {
    id: '1-item',
    name: 'Item Survey 1',
    type: 'itemSurvey',
    linkUrl: 'https://www.abc.com',
    deadline: '2023-07-26T06:59:00.000Z',
    isOverdue: false,
    status: 'started',
    project: mockProject,
    isVisible: true,
    surveyId: '2-survey-id',
    isLegacy: false,
  },
];
